// @core/stores/admin/AdminStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { adminApi } from '@core/api';
import type {
  AdminSettingsResponse,
  AdminStatsResponse,
  AuditLogEntry,
  ExportAuditLogsRequest,
  ExportAuditLogsResponse,
  UpdateAdminSettingsRequest,
} from '@core/types/admin';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class AdminStore {
  // List state
  items: AuditLogEntry[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: AuditLogEntry | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, AuditLogEntry>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): AuditLogEntry | undefined {
    return this.cache.get(id);
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private updateCache(_item: AuditLogEntry | undefined): void {
    // Entity type does not have entity.id field — override with custom cache key logic
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Export audit logs to file
   */
  async ExportAuditLogs(data: ExportAuditLogsRequest): Promise<ExportAuditLogsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await adminApi.ExportAuditLogs(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get admin settings
   */
  async GetAdminSettings(params?: { }): Promise<AdminSettingsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await adminApi.GetAdminSettings(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get admin dashboard statistics
   */
  async GetAdminStats(params?: { }): Promise<AdminStatsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await adminApi.GetAdminStats(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get specific audit log entry
   */
  async GetAuditLog(log_id: string): Promise<AuditLogEntry> {
    // Check cache first
    const cached = this.cache.get(log_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await adminApi.GetAuditLog(log_id);
      const item = response.log;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List audit logs across all services
   */
  async ListAuditLogs(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await adminApi.ListAuditLogs(params);

      runInAction(() => {
        this.items = response.logs?.entries ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List audit logs by actor (user/service)
   */
  async ListAuditLogsByActor(actor_id: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await adminApi.ListAuditLogsByActor(actor_id, params);

      runInAction(() => {
        this.items = response.logs?.entries ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List audit logs for specific resource
   */
  async ListAuditLogsByResource(resource_type: string, resource_id: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await adminApi.ListAuditLogsByResource(resource_type, resource_id, params);

      runInAction(() => {
        this.items = response.logs?.entries ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update admin settings
   */
  async UpdateAdminSettings(data: UpdateAdminSettingsRequest): Promise<AdminSettingsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await adminApi.UpdateAdminSettings(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const adminStore = new AdminStore();
